import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    User, Users, Building2, ArrowRight, ShieldCheck, FileCheck2, Mail,
    MessageCircle, Sparkles, Clock,
} from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { LOGO_LIGHT_BG } from "@/lib/brand";
import { waLink } from "@/lib/brand";

// Only Channel Partner has a live application form today. Buyer & Builder
// applications route to a "coming soon / apply via desk" experience — no one
// can self-signup any more (per platform trust-first policy).
const ROLES = [
    {
        k: "partner",
        l: "Channel Partner",
        icon: Users,
        desc: "Broker, consultant or agency team lead",
        cta: "Start Partner Application",
        route: "/partner-registration",
        available: true,
    },
    {
        k: "buyer",
        l: "Buyer / Investor",
        icon: User,
        desc: "Individual, NRI or family office",
        cta: "Request Onboarding",
        route: null,
        available: false,
    },
    {
        k: "builder",
        l: "Builder / Developer",
        icon: Building2,
        desc: "Promoter, developer or channel-sales owner",
        cta: "Request Onboarding",
        route: null,
        available: false,
    },
];

export default function Register() {
    const loc = useLocation();
    const nav = useNavigate();
    const initial = new URLSearchParams(loc.search).get("role") || "partner";
    const [role, setRole] = useState(initial);

    // Deep-link ?role=partner → auto-route to the dedicated CP form so an old
    // /register?role=partner link keeps working without a manual click.
    useEffect(() => {
        if (initial === "partner") nav("/partner-registration", { replace: true });
    }, [initial, nav]);

    const active = ROLES.find((r) => r.k === role) || ROLES[0];

    return (
        <>
            <Nav variant="light"/>
            <main className="min-h-screen bg-[var(--ebg-ivory)] pt-28 pb-24" data-testid="register-page">
                <div className="max-w-5xl mx-auto px-4 md:px-6">
                    <header className="text-center mb-10 md:mb-14">
                        <Link to="/">
                            <img src={LOGO_LIGHT_BG} alt="EBG Realty" className="h-11 mx-auto opacity-95 mb-6"/>
                        </Link>
                        <div className="text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Join EBG Realty</div>
                        <h1 className="font-serif text-4xl md:text-6xl mt-3 leading-[1.05]">
                            Apply for your role &mdash;<br/>
                            <em className="not-italic text-[var(--ebg-gold)]">approved by our team.</em>
                        </h1>
                        <p className="text-black/60 mt-5 max-w-2xl mx-auto text-sm md:text-base">
                            EBG Realty is a trust-first platform. Every account is manually reviewed and verified by our
                            partner-relations desk before activation &mdash; no automatic sign-ups.
                        </p>
                    </header>

                    {/* Role selector */}
                    <div className="grid md:grid-cols-3 gap-4 md:gap-5" data-testid="register-roles">
                        {ROLES.map((r) => {
                            const isActive = role === r.k;
                            return (
                                <button
                                    key={r.k}
                                    onClick={() => setRole(r.k)}
                                    data-testid={`register-role-${r.k}`}
                                    className={`text-left p-6 border-2 transition-colors ${isActive ? "border-[var(--ebg-gold)] bg-white shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]" : "border-black/10 bg-white hover:border-black/40"}`}
                                >
                                    <div className={`w-11 h-11 rounded-full flex items-center justify-center ${isActive ? "bg-[var(--ebg-gold)] text-black" : "bg-black/5 text-black/60"}`}>
                                        <r.icon size={18}/>
                                    </div>
                                    <div className="text-[11px] uppercase tracking-[0.24em] font-bold mt-4">{r.l}</div>
                                    <div className="text-[13px] text-black/60 mt-1">{r.desc}</div>
                                    <div className={`text-[10px] uppercase tracking-[0.2em] font-bold mt-5 inline-flex items-center gap-1.5 ${r.available ? "text-emerald-700" : "text-black/40"}`}>
                                        {r.available ? <><Sparkles size={11}/> Open for applications</> : <><Clock size={11}/> By invitation</>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Action panel */}
                    <div className="bg-white border border-black/8 mt-8 md:mt-10 p-6 md:p-10" data-testid="register-action-panel">
                        {active.available ? (
                            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                                <div className="w-14 h-14 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center shrink-0">
                                    <FileCheck2 size={22} className="text-[var(--ebg-gold)]"/>
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)]">Ready when you are</div>
                                    <h2 className="font-serif text-2xl md:text-3xl mt-2">Start your {active.l} application</h2>
                                    <p className="text-black/60 mt-2 text-[14px] leading-relaxed">
                                        A 5-step form takes ~10 minutes. Once submitted, our team reviews and gets back within <b>2 business days</b>.
                                    </p>
                                </div>
                                <button
                                    onClick={() => nav(active.route)}
                                    data-testid="register-cta-primary"
                                    className="shrink-0 inline-flex items-center gap-2 bg-black text-white px-6 md:px-8 py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black"
                                >
                                    {active.cta} <ArrowRight size={14}/>
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                                <div className="w-14 h-14 rounded-full bg-black/5 border border-black/15 flex items-center justify-center shrink-0">
                                    <Clock size={22} className="text-black/60"/>
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/50">By invitation only</div>
                                    <h2 className="font-serif text-2xl md:text-3xl mt-2">{active.l} onboarding is by invitation</h2>
                                    <p className="text-black/60 mt-2 text-[14px] leading-relaxed">
                                        To keep our platform trust-first, {active.l.toLowerCase()} accounts are opened only after a short conversation
                                        with our desk. Send us a note and we&rsquo;ll set up a 15-min call within 24 hours.
                                    </p>
                                </div>
                                <div className="shrink-0 flex flex-col sm:flex-row gap-2">
                                    <a
                                        href={`mailto:enquiry@ebgrealty.in?subject=${encodeURIComponent(`${active.l} onboarding request`)}&body=${encodeURIComponent(`Hi EBG Realty team,\n\nI'd like to open a ${active.l} account. My contact details:\n- Name:\n- Company:\n- City:\n- Phone:\n\nBest regards,`)}`}
                                        data-testid="register-cta-email"
                                        className="inline-flex items-center justify-center gap-2 bg-black text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black"
                                    >
                                        <Mail size={13}/> Email Us
                                    </a>
                                    <a
                                        href={waLink(`Hi EBG Realty, I'd like to open a ${active.l} account.`)}
                                        target="_blank" rel="noreferrer"
                                        data-testid="register-cta-whatsapp"
                                        className="inline-flex items-center justify-center gap-2 border border-black text-black px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-white"
                                    >
                                        <MessageCircle size={13}/> WhatsApp
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Trust strip */}
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-black/50">
                        <span className="flex items-center gap-2"><ShieldCheck size={12} className="text-[var(--ebg-gold)]"/> Manually Reviewed</span>
                        <span className="flex items-center gap-2"><FileCheck2 size={12} className="text-[var(--ebg-gold)]"/> Legal-Verified Platform</span>
                        <span className="flex items-center gap-2"><Sparkles size={12} className="text-[var(--ebg-gold)]"/> Trust-First Onboarding</span>
                    </div>

                    <div className="mt-8 text-center text-[13px] text-black/60">
                        Already have an account?{" "}
                        <Link to="/login" className="text-[var(--ebg-gold)] font-bold hover:text-black inline-flex items-center gap-1">
                            Sign in <ArrowRight size={11}/>
                        </Link>
                    </div>
                </div>
            </main>
            <Footer/>
        </>
    );
}
